import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Briefcase, Home, Clock, Eye, Heart, ChevronRight, Facebook, Instagram, Search } from 'lucide-react';
import Footer from '@/components/Footer';
import { API } from '../App';

const categories = [
  { id: 'all', label: 'Todos' },
  { id: 'consejos', label: 'Consejos' },
  { id: 'guias', label: 'Guías' },
  { id: 'noticias', label: 'Noticias' },
  { id: 'testimonios', label: 'Testimonios' }
];

const BlogListPage = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/blog`
        : `${API}/blog?category=${selectedCategory}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short'
    });
  };

  const getCategoryStyle = (category) => {
    const styles = {
      consejos: 'bg-blue-500',
      guias: 'bg-emerald-500',
      noticias: 'bg-amber-500',
      testimonios: 'bg-purple-500'
    };
    return styles[category] || 'bg-slate-500';
  };

  const featuredPost = posts[0];
  const gridPosts = posts.slice(1);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white z-50 border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              <span className="text-lg sm:text-2xl font-bold text-slate-900">JobSuiza</span>
            </Link>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="home-btn">
                  <Home className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Inicio</span>
                </Button>
              </Link>
              <Link to="/ofertas">
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                  Ver Ofertas
                </Button>
              </Link>
              
              <div className="hidden sm:flex items-center space-x-2">
                <a 
                  href="https://www.facebook.com/jobsuizaoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a 
                  href="https://www.instagram.com/jobsuizaoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>

              {user ? (
                <Link to="/dashboard">
                  <Button variant="outline" size="sm">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button variant="outline" size="sm">Entrar</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 pb-8 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <span className="inline-block px-4 py-1 bg-red-100 text-red-600 rounded-full text-sm font-medium mb-4">
            Blog
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Guías y Consejos para <span className="text-red-600">Trabajar en Suiza</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Todo lo que necesitas saber para dar el salto profesional a Suiza como hispanohablante
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-4 py-6 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === cat.id
                    ? 'bg-slate-900 text-white shadow-lg scale-105'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="mt-4 text-slate-500">Cargando artículos...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500 text-lg">No hay artículos en esta categoría</p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {featuredPost && (
                <Link 
                  to={`/blog/${featuredPost.slug}`}
                  className="group block mb-10"
                  data-testid="featured-post"
                >
                  <article className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                    <div className="grid md:grid-cols-2">
                      {/* Image */}
                      <div className="relative h-64 md:h-96 overflow-hidden">
                        {featuredPost.cover_image ? (
                          <img 
                            src={featuredPost.cover_image} 
                            alt={featuredPost.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-red-500 to-red-700" />
                        )}
                        <div className="absolute top-4 left-4">
                          <span className={`px-3 py-1 ${getCategoryStyle(featuredPost.category)} text-white text-xs font-bold uppercase rounded-full`}>
                            {featuredPost.category}
                          </span>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-6 md:p-10 flex flex-col justify-center">
                        <span className="text-red-600 font-semibold text-sm mb-2">Artículo Destacado</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 group-hover:text-red-600 transition-colors leading-tight">
                          {featuredPost.title}
                        </h2>
                        <p className="text-slate-600 mb-6 line-clamp-3 leading-relaxed">
                          {featuredPost.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatDate(featuredPost.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {featuredPost.views || 0}
                            </span>
                          </div>
                          <span className="inline-flex items-center gap-1 text-red-600 font-medium group-hover:gap-2 transition-all">
                            Leer más <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              )}

              {/* Grid of Articles */}
              {gridPosts.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gridPosts.map((post) => (
                    <Link 
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="group"
                      data-testid={`blog-post-${post.slug}`}
                    >
                      <article className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden">
                          {post.cover_image ? (
                            <img 
                              src={post.cover_image} 
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600" />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute top-3 left-3">
                            <span className={`px-2 py-1 ${getCategoryStyle(post.category)} text-white text-xs font-bold uppercase rounded-full`}>
                              {post.category}
                            </span>
                          </div>
                          <div className="absolute bottom-3 left-3 right-3">
                            <span className="text-white/80 text-xs flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(post.created_at)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-5 flex-1 flex flex-col">
                          <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors leading-snug line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-slate-500 text-sm mb-4 line-clamp-2 flex-1">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {post.views || 0}
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" />
                                {post.likes || 0}
                              </span>
                            </div>
                            <span className="text-red-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                              Leer <ChevronRight className="w-3 h-3" />
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            ¿Listo para trabajar en Suiza?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Explora cientos de ofertas de trabajo que buscan hispanohablantes como tú
          </p>
          <Link to="/ofertas">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 text-lg">
              <Search className="w-5 h-5 mr-2" />
              Ver Ofertas de Trabajo
            </Button>
          </Link>
        </div>
      </section>

      {/* Admin Link */}
      {user?.role === 'admin' && (
        <section className="py-6 px-4 bg-slate-100">
          <div className="max-w-6xl mx-auto text-center">
            <Link to="/blog/editor">
              <Button variant="outline" className="border-slate-400">
                + Crear Nuevo Artículo
              </Button>
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogListPage;
